import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { type NewUserTransactionDialogViewModel } from '@/features/payouts/admin/review/NewUserTransactionDialogViewModel'
import { NewUserTransactionModal } from '@/features/payouts/admin/review/components/NewUserTransactionModal'
import { FormGroupContainer } from '@/components/FormContainer'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import {
  CustomSelect,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { Avatar } from '@/components/Avatar'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { ToggleGroup, ToggleItem } from '@/components/ToggleGroup'
import { formatTransactionReason } from '@/features/payouts/transaction-formatters'
import { ConfirmNewTransactionModalContainer } from '@/features/payouts/admin/review/components/ConfirmNewTransactionModal/ConfirmNewTransactionModalContainer'

export interface NewUserTransactionModalContainerProps {
  vm: NewUserTransactionDialogViewModel
}

export const NewUserTransactionModalContainer = observer(
  function NewUserTransactionModalContainer({
    vm,
  }: NewUserTransactionModalContainerProps) {
    return (
      <NewUserTransactionModal
        showing={vm.modal.showing}
        isCreatePending={vm.createTransaction.pending}
        onClose={vm.modal.close}
        onSubmit={vm.createTransaction}
        hasValidationErrors={!vm.validation.isValid}
        error={vm.createTransaction.error}
        workspaceSelector={<WorkspaceSelectorContainer vm={vm} />}
        recipientSelector={<RecipientSelectorContainer vm={vm} />}
        amountInput={<AmountInputContainer vm={vm} />}
        transactionTypeSelector={<TransactionTypeSelectorContainer vm={vm} />}
        transactionReasonSelector={
          <TransactionReasonSelectorContainer vm={vm} />
        }
        memoInput={<MemoInputContainer vm={vm} />}
        confirmModal={
          <ConfirmNewTransactionModalContainer vm={vm.confirmationVm} />
        }
      />
    )
  },
)

interface WorkspaceSelectorContainerProps {
  vm: NewUserTransactionDialogViewModel
}

const WorkspaceSelectorContainer = observer(
  function WorkspaceSelectorContainer({ vm }: WorkspaceSelectorContainerProps) {
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource: vm.searchWorkspaces,
    })
    const error = vm.validation.getError('selectedWorkspace')
    return (
      <FormGroupContainer
        label="Workspace"
        error={error}
        help="The transaction will be attributed to this workspace"
      >
        <CustomSelect
          {...selectProps}
          status={error ? 'danger' : 'default'}
          placeholder="Select a Workspace"
          itemToString={(v) => String(v?.name)}
          isDisabled={vm.disabled}
          value={vm.selectedWorkspace ?? undefined}
          onChange={vm.selectWorkspace}
          multi={false}
          testId="new-transaction-recipient-selector"
          renderItem={(itemProps) => (
            <SingleItemSelectedDefault
              {...itemProps}
              visual={
                <Avatar
                  size={24}
                  subject={{
                    displayName: itemProps.item.name,
                    avatar: itemProps.item.branding.logos.icon,
                  }}
                />
              }
            />
          )}
          renderSearchItem={({
            item,
            itemToString: _itemToString,
            ...itemProps
          }) => (
            <SearchResultItem
              {...itemProps}
              visual={
                <Avatar
                  size={24}
                  subject={{
                    avatar: item.branding.logos.icon,
                    displayName: item.name,
                  }}
                />
              }
            >
              {item.name}
            </SearchResultItem>
          )}
        />
      </FormGroupContainer>
    )
  },
)

interface RecipientSelectorContainerProps {
  vm: NewUserTransactionDialogViewModel
}

const RecipientSelectorContainer = observer(
  function RecipientSelectorContainer({ vm }: RecipientSelectorContainerProps) {
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource: vm.searchMembers,
      debounce: 300,
    })
    const error = vm.validation.getError('selectedMember')
    return (
      <FormGroupContainer
        label="Recipient"
        error={error}
        help="The user that the transaction affects; must be a member of the selected workspace"
      >
        <CustomSelect
          {...selectProps}
          status={error ? 'danger' : 'default'}
          placeholder="Select a Workspace Member"
          itemToString={(v) => String(v?.displayName)}
          isDisabled={vm.disabled || !vm.selectedWorkspace}
          value={vm.selectedMember ?? undefined}
          onChange={vm.selectMember}
          multi={false}
          testId="new-transaction-recipient-selector"
          renderItem={(itemProps) => (
            <SingleItemSelectedDefault
              {...itemProps}
              visual={<Avatar size={24} subject={itemProps.item} />}
            />
          )}
          renderSearchItem={({
            item,
            itemToString: _itemToString,
            ...itemProps
          }) => (
            <SearchResultItem
              {...itemProps}
              visual={<Avatar size={24} subject={item} />}
            >
              {item.displayName || `(User ID: ${item.user.publicId})`}
            </SearchResultItem>
          )}
        />
      </FormGroupContainer>
    )
  },
)

interface TransactionTypeSelectorContainerProps {
  vm: NewUserTransactionDialogViewModel
}

const TransactionTypeSelectorContainer = observer(
  function TransactionTypeSelectorContainer({
    vm,
  }: TransactionTypeSelectorContainerProps) {
    return (
      <FormGroupContainer label="Type">
        <ToggleGroup stretch>
          <ToggleItem
            isDisabled={vm.disabled}
            toggled={vm.transactionType === 'Payment'}
            onChange={vm.selectPaymentType}
          >
            Payment
          </ToggleItem>
          <ToggleItem
            isDisabled={vm.disabled}
            toggled={vm.transactionType === 'Abatement'}
            onChange={vm.selectAbatementType}
          >
            Abatement
          </ToggleItem>
        </ToggleGroup>
      </FormGroupContainer>
    )
  },
)

interface TransactionReasonSelectorContainerProps {
  vm: NewUserTransactionDialogViewModel
}

const TransactionReasonSelectorContainer = observer(
  function TransactionReasonSelectorContainer({
    vm,
  }: TransactionReasonSelectorContainerProps) {
    const error = vm.validation.getError('reason')
    const itemSource = useMemo(
      () =>
        ItemSource.static(vm.availableReasons, (item, text) =>
          formatTransactionReason(item)
            .toLowerCase()
            .includes(text.toLowerCase()),
        ),
      [vm.availableReasons],
    )
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })
    return (
      <FormGroupContainer
        label="Reason"
        error={error}
        help="Specify a reason for this transaction"
      >
        <CustomSelect
          {...selectProps}
          status={error ? 'danger' : 'default'}
          placeholder="Select a reason"
          itemToString={(v) => (v ? formatTransactionReason(v) : '')}
          isDisabled={vm.disabled}
          value={vm.reason ?? undefined}
          onChange={vm.selectReason}
          multi={false}
          testId="new-transaction-reason-selector"
        />
      </FormGroupContainer>
    )
  },
)

interface AmountInputContainerProps {
  vm: NewUserTransactionDialogViewModel
}

const AmountInputContainer = observer(function AmountInputContainer({
  vm,
}: AmountInputContainerProps) {
  const error = vm.validation.getError('amount')
  return (
    <NumericInputContainer
      label="Amount"
      placeholder="The transaction amount"
      help="The positive transaction amount"
      currencyCode="usd"
      model={vm}
      field="amount"
      precision={2}
      isDisabled={vm.disabled}
      status={error ? 'danger' : 'default'}
      error={error}
    />
  )
})

interface MemoInputContainerProps {
  vm: NewUserTransactionDialogViewModel
}

const MemoInputContainer = observer(function MemoInputContainer({
  vm,
}: MemoInputContainerProps) {
  const error = vm.validation.getError('memo')
  return (
    <TextAreaContainer
      label="Memo"
      placeholder="Optional description for the transaction"
      model={vm}
      isDisabled={vm.disabled}
      status={error ? 'danger' : 'default'}
      field="memo"
      error={error}
      help="Max 280 characters"
    />
  )
})
